import React, {useCallback, useContext, useEffect, useRef, useState} from "react"
import RefreshTimer from "../../util/RefreshTimer";
import {AsmlDeviceOverviewContext} from "./context/AsmlDeviceOverviewContext";
import Row from "@meridian/components/row";
import {
    getAssetRefreshInterval, getChildDeviceBySubType,
    getStatusRefreshInterval, sortComparator
} from "../../util/Util";
import {FormattedMessage, injectIntl} from "react-intl";
import Button from "@amzn/meridian/button";
import Menu, {MenuItem} from "@amzn/meridian/menu";
import Icon from "@amzn/meridian/icon";
import Column from "@amzn/meridian/column";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import {
    ACTIVE,
    ASCENDING,
    BLUE_COLOR,
    DEFAULT_VENDOR_ATTRIBUTES,
    ORANGE_COLOR,
    PRINTER_SUB_ASSET_TYPE,
    UNASSIGN_VERIFICATION_CAMERA,
    VERIFICATION_CAMERA_SUB_ASSET_TYPE,
    UNASSIGN_PRINTER,
    NO_PRINTER_ASSIGNED, NO_VERIFICATION_CAMERA_ASSIGNED
} from "../../util/Constants";
import Text from "@amzn/meridian/text";
import Box from "@amzn/meridian/box";
import Loader from "../../loader/Loader";
import Tooltip from "@amzn/meridian/tooltip";
import chevronDownLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-down-large";
import Alert from "@amzn/meridian/alert";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import {getLocalizedMessage} from "../../util/Localization";
import AddAsmlSingleDeviceModal from "./modal/AddAsmlSingleDeviceModal";

const AsmlDeviceOverview = ({intl}) => {

    const {viewModel, dispatch} = useContext(AsmlDeviceOverviewContext);
    const [openActionsMenu, setOpenActionsMenu] = useState({});
    const [openAddModal, setOpenAddModal] = useState(false)
    const [selectedInductCamera, setSelectedInductCamera] = useState("")
    const [selectedPrinter, setSelectedPrinter] = useState("")
    const [selectedVerificationCamera, setSelectedVerificationCamera] = useState("")
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const actionButtonRefs = useRef([]);
    const tableEndRef = useRef(null)

    const onClickActionsButton = useCallback((index) => {
        let _open = {...openActionsMenu};
        _open[index] = true;
        setOpenActionsMenu(_open);
    }, []);
    const onCloseActions = useCallback((index) => {
        let _open = {...openActionsMenu};
        _open[index] = false;
        setOpenActionsMenu(_open)
    }, []);

    useEffect(() => {
        if (!openAddModal && !openDeleteModal) {
            getData()
            const interval = setInterval(() => {
                if (!document.hidden) {
                    getData();
                }
            }, getAssetRefreshInterval());
            const statusInterval = setInterval(() => {
                if (!document.hidden) {
                    getStatusData();
                }
            }, getStatusRefreshInterval());
            return () => {
                clearInterval(interval);
                clearInterval(statusInterval);
            }
        }
    }, [openAddModal, openDeleteModal]);
    useEffect(() => {
        actionButtonRefs.current = actionButtonRefs.current.slice(0, viewModel.inductCameraDeviceInformation.length);
    }, [viewModel.inductCameraDeviceInformation]);

    const onSort = (sortDetails) => {
        dispatch({
            type: 'SORT_DATA',
            data: {column: sortDetails.sortColumn, direction: sortDetails.sortDirection}
        })
    }
    const sortedDeviceList = Object.values(viewModel.inductCameraDeviceInformation);
    sortedDeviceList && sortedDeviceList.sort((a, b) => {
        const {column, direction} = viewModel.sortPref;
        let compareValue;
        if (Object.keys(DEFAULT_VENDOR_ATTRIBUTES).includes(column)) {
            if(column === "vendor"){
                compareValue = sortComparator(a.vendorAttributes[column]+a.vendorAttributes["spec_version"], b.vendorAttributes[column]+b.vendorAttributes["spec_version"]);
            } else{
                compareValue = sortComparator(a.vendorAttributes[column], b.vendorAttributes[column]);
            }
        } else {
            compareValue = sortComparator(a[column], b[column])
        }
        if (direction === ASCENDING) {
            return compareValue;
        } else {
            return -compareValue;
        }
    })

    const getData = () => {
        setOpenAddModal(false)
        setOpenDeleteModal(false)
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }
    const getStatusData = () => {
        dispatch({type: 'LOAD_STATUS', data: {dispatcher: dispatch}});
    }
    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }
    const onClickAddEditDevice = () => {
        setSelectedInductCamera("");
        setSelectedPrinter("")
        setSelectedVerificationCamera("")
        setOpenAddModal(true);
    }
    const onClickEditDevice = (inductCameraMac, printerMac, verificationCameraMac) => {
        setSelectedInductCamera(inductCameraMac)
        setSelectedPrinter(printerMac)
        setSelectedVerificationCamera(verificationCameraMac)
        setOpenAddModal(true)
    }

    const getAsmlStatusDisplayContent = (childComponent) => {
        if (childComponent === null) {
            return (
                <br></br>
            )
        } else {
            if (childComponent.assetStatus) {
                return (
                    <p style={{color: childComponent.assetStatus.toUpperCase() === ACTIVE ? BLUE_COLOR : ORANGE_COLOR}}>
                        <b>{childComponent.assetStatus}</b>
                        <br></br>
                    </p>
                )
            } else {
                return (
                    <b><FormattedMessage id="loading.text" defaultMessage="Loading..."/></b>
                )
            }
        }
    }

    const getAvailableMacAddressList = (allMacAddressList, usedMacAddressList) => {
        const availableMacAddressList = allMacAddressList.filter(x => !usedMacAddressList.includes(x));
        return availableMacAddressList
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    } else {
        return (
            <Row widths="100%"
                 alignmentVertical="top"
                 spacingInset="none none large none">
                <Column>
                    <Row width="100%" widths={["100", "fill", "100"]}>
                        <RefreshTimer deps={[viewModel]}/>
                        <Box></Box>
                        <Tooltip position="top" title={intl.formatMessage({
                            id: 'tooltip.scrolltobottom',
                            defaultMessage: 'Scroll to bottom'
                        })}>
                            <Button onClick={scrollToBottom}
                                    type="secondary"
                                    size="small">
                                <Icon tokens={chevronDownLargeTokens}/>
                            </Button>
                        </Tooltip>
                    </Row>
                    {sortedDeviceList.length === 0 ?
                        <Alert type="informational" size="small">
                            <FormattedMessage id="alert.device"
                                              defaultMessage="No device present for this station."/>
                        </Alert> :
                        <Table
                            headerRows={1}
                            spacing="small"
                            sortColumn={viewModel.sortPref.column}
                            sortDirection={viewModel.sortPref.direction}
                            onSort={onSort}
                            showDividers={true}
                            showStripes={true}>
                            <TableRow
                                highlightOnHover={false}>
                                <TableCell sortColumn="name">
                                    <FormattedMessage id="device.name" defaultMessage="Name"/>
                                </TableCell>
                                <TableCell sortColumn="mac">
                                    <FormattedMessage id="device.component_type" defaultMessage="Component Type"/>
                                </TableCell>
                                <TableCell sortColumn="mac">
                                    <FormattedMessage id="device.mac_ip" defaultMessage="MAC/IP"/>
                                </TableCell>
                                <Tooltip position={"top"} title={getLocalizedMessage({intl, stringId: 'tooltip.connectionstatus'})}>
                                    <TableCell sortColumn="connection">
                                        <FormattedMessage id="device.connection" defaultMessage="Connection Status"/>
                                    </TableCell>
                                </Tooltip>
                                <TableCell>
                                    <FormattedMessage id="device.action" defaultMessage="Action"/>
                                </TableCell>
                            </TableRow>
                            {sortedDeviceList.map((inductCameraDevice, index) => {
                                const printerChildAsset = getChildDeviceBySubType(inductCameraDevice.mac, Object.values(viewModel.printerDeviceInformation),
                                    PRINTER_SUB_ASSET_TYPE);
                                const printerDevice = printerChildAsset.childAsset
                                const verificationCameraChildAsset = getChildDeviceBySubType(inductCameraDevice.mac, Object.values(viewModel.verificationCameraDeviceInformation),
                                    VERIFICATION_CAMERA_SUB_ASSET_TYPE);
                                const verificationCameraDevice = verificationCameraChildAsset.childAsset;
                                return (<TableRow key={index}
                                                  highlightOnHover={true}>
                                        <TableCell alignmentVertical="top">
                                            <b>{inductCameraDevice.name}</b>
                                            <br></br>
                                            {inductCameraDevice.deviceType}
                                        </TableCell>
                                        <TableCell alignmentVertical="top">
                                            <Row alignmentVertical="top">
                                                <Column alignmentVertical="top">
                                                    <b>INDUCT CAMERA</b>
                                                </Column>
                                            </Row>
                                            <br></br>
                                            {printerDevice !== null ?
                                            <Row alignmentVertical="top">
                                                <Column alignmentVertical="top">
                                                    <b>PRINTER</b>
                                                </Column>
                                            </Row> : <b/>
                                            }
                                            <br></br>
                                            { verificationCameraDevice !== null ?
                                            <Row alignmentVertical="top">
                                                <Column alignmentVertical="top">
                                                    <b>VERIFICATION CAMERA</b>
                                                </Column>
                                            </Row> : <br/>
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <Row alignmentVertical="top">
                                                <Column alignmentVertical="top">
                                                    <b>{inductCameraDevice.mac}</b>
                                                    {inductCameraDevice.ip}:{inductCameraDevice.port}
                                                </Column>
                                            </Row>
                                            {printerDevice !== null ?
                                                <Row alignmentVertical="top">
                                                    <Column alignmentVertical="top">
                                                        <b>{printerDevice.mac}</b>
                                                        {printerDevice.ip}:{printerDevice.port}
                                                    </Column>
                                                </Row> :
                                                <br/>
                                            }
                                            { verificationCameraDevice !== null ?
                                                <Row alignmentVertical="top">
                                                    <Column alignmentVertical="top">
                                                        <b>{verificationCameraDevice.mac}</b>
                                                        {verificationCameraDevice.ip}:{verificationCameraDevice.port}
                                                    </Column>
                                                </Row> :
                                                <br/>
                                            }
                                        </TableCell>
                                        <TableCell alignmentVertical="top">
                                            {
                                                getAsmlStatusDisplayContent(inductCameraDevice)
                                            }
                                            {
                                                getAsmlStatusDisplayContent(printerDevice)
                                            }
                                            {
                                                getAsmlStatusDisplayContent(verificationCameraDevice)
                                            }
                                        </TableCell>
                                        <TableCell alignmentVertical="top">
                                            <Text type="b300">
                                                <Button ref={el => actionButtonRefs.current[index] = el}
                                                        type="icon"
                                                        size="medium"
                                                        onClick={() => onClickActionsButton(index)}
                                                        aria-controls="my-menu"
                                                        aria-haspopup="menu"
                                                        aria-expanded="false">
                                                    ...
                                                </Button>
                                                <Menu anchorNode={actionButtonRefs.current[index]}
                                                      open={openActionsMenu[index] ? openActionsMenu[index] : false}
                                                      position="left"
                                                      onClose={() => onCloseActions(index)}>
                                                    <MenuItem onClick={() => onClickEditDevice(inductCameraDevice.mac, printerDevice ? printerDevice.mac : NO_PRINTER_ASSIGNED, verificationCameraDevice ? verificationCameraDevice.mac : NO_VERIFICATION_CAMERA_ASSIGNED)}>
                                                        <FormattedMessage id="actions.addDevice"
                                                                          defaultMessage="Edit"/>
                                                    </MenuItem>
                                                </Menu>
                                            </Text>
                                        </TableCell>
                                    </TableRow>
                                )})}
                        </Table>}
                    <Row width="100%" widths={["100", "fill", "100"]}>
                        <Button onClick={onClickAddEditDevice} type="primary" size="small">
                            <Icon tokens={plusCircleTokens}/>
                            <FormattedMessage id="device.add_device_button_modal" defaultMessage="Add Device Configuration"/>
                        </Button>
                        <Box></Box>
                    </Row>
                    <AddAsmlSingleDeviceModal open={openAddModal} setOpen={setOpenAddModal}
                                              availableInductCameraMacAddressList={getAvailableMacAddressList(viewModel.allInductCameraMacAddressArray,
                        viewModel.usedInductCameraMacAddressArray)} availablePrinterMacAddressList={getAvailableMacAddressList(viewModel.allPrinterMacAddressArray,
                        viewModel.usedPrinterMacAddressArray)} availableVerificationCameraMacAddressList={getAvailableMacAddressList(viewModel.allVerificationCameraMacAddressArray,
                        viewModel.usedVerificationCameraMacAddressArray)} selectedInductCameraMac={selectedInductCamera} setSelectedInductCameraMac={setSelectedInductCamera}
                        selectedPrinterMac={selectedPrinter} setSelectedPrinterMac={setSelectedPrinter}
                        selectedVerificationCameraMac={selectedVerificationCamera} setSelectedVerificationCameraMac={setSelectedVerificationCamera}
                        editAvailableInductCameraMacAddressList={[...getAvailableMacAddressList(viewModel.allInductCameraMacAddressArray, viewModel.usedInductCameraMacAddressArray), selectedInductCamera]}
                        editAvailablePrinterMacAddressList={[...getAvailableMacAddressList(viewModel.allPrinterMacAddressArray, viewModel.usedPrinterMacAddressArray), selectedPrinter, UNASSIGN_PRINTER]}
                        editAvailableVerificationCameraMacAddressList={[...getAvailableMacAddressList(viewModel.allVerificationCameraMacAddressArray, viewModel.usedVerificationCameraMacAddressArray), selectedVerificationCamera, UNASSIGN_VERIFICATION_CAMERA]}
                    />
                    <div ref={tableEndRef}/>
                </Column>
            </Row>
        );
    }
}

export default injectIntl(AsmlDeviceOverview)



// WEBPACK FOOTER //
// ./src/components/devicemanagement/asmldevice/AsmlDeviceOverview.js